/**
 * VeriTablosu — **uzuv** mertebesi. TanStack Table üzerine kurulu,
 * **sunucu taraflı** (server-side) tablo.
 *
 * Sunucu taraflı ne demek: sayfalama, sıralama ve filtreleme tarayıcıda
 * DEĞİL veritabanında yapılır. TanStack `manualPagination`/`manualSorting`
 * kipinde çalışır — yani "elindeki satırları sırala" demez, "sunucu ne
 * verdiyse onu göster" der.
 *
 * Neden zorunlu: 105 fatura bugün tarayıcıya sığar, 100.000 fatura sığmaz.
 * İstemci taraflı tablo tüm kayıtları indirmek zorundadır; bu hem yavaştır
 * hem de tüm müşteri kimlik verisini gereksizce tarayıcıya taşır (KVKK).
 *
 * Duyarlılık: **tek bileşen, iki görünüm.** Geniş kapta tablo, dar kapta
 * kart listesi. "Mobil sürüm" diye ikinci bir bileşen YAZILMAZ (docs/13
 * bölüm 3) — aynı kolon tanımları her iki görünümü de besler.
 */

import type { ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type Row,
  type RowData,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/cn';
import { Dugme } from './ui/Dugme';
import { BosDurum, Yukleniyor } from './Durumlar';

export interface SayfaBilgisi {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface VeriTablosuProps<T> {
  veri: T[];
  kolonlar: ColumnDef<T, unknown>[];
  sayfa: SayfaBilgisi;
  siralama: SortingState;
  yukleniyor: boolean;
  /** Arka planda tazeleniyor — tablo görünür kalır, üstte ince çizgi akar. */
  tazeleniyor?: boolean;
  onSiralama: (siralama: SortingState) => void;
  onSayfa: (sayfa: number) => void;
  onSayfaBoyu: (boy: number) => void;
  /** Satıra tıklanınca gidilecek yer — kart görünümünde de aynısı çalışır. */
  onSatirTikla?: (satir: T) => void;
  /** Kart görünümünün (dar kap) gövdesi. Verilmezse kartlar kolonlardan üretilir. */
  kartGoster?: (satir: T) => ReactNode;
  bosBaslik: string;
  bosAciklama: string;
  bosEylem?: ReactNode;
  /** Satır anahtarı — index KULLANILMAZ (sayfa değişince React satırları karıştırır). */
  satirAnahtari: (satir: T) => string;
}

const SAYFA_BOYLARI = [10, 25, 50, 100] as const;

export function VeriTablosu<T>({
  veri,
  kolonlar,
  sayfa,
  siralama,
  yukleniyor,
  tazeleniyor = false,
  onSiralama,
  onSayfa,
  onSayfaBoyu,
  onSatirTikla,
  kartGoster,
  bosBaslik,
  bosAciklama,
  bosEylem,
  satirAnahtari,
}: VeriTablosuProps<T>) {
  const tablo = useReactTable({
    data: veri,
    columns: kolonlar,
    getCoreRowModel: getCoreRowModel(),
    // Sıralama ve sayfalama SUNUCUDA: TanStack elindeki satırları yeniden
    // sıralamaya kalkarsa ekranda sunucudan farklı bir sıra görünür.
    manualSorting: true,
    manualPagination: true,
    pageCount: sayfa.totalPages,
    state: { sorting: siralama },
    onSortingChange: (guncelleyici) => {
      const yeni = typeof guncelleyici === 'function' ? guncelleyici(siralama) : guncelleyici;
      onSiralama(yeni);
    },
  });

  const ilkKayit = (sayfa.page - 1) * sayfa.pageSize + 1;
  const sonKayit = Math.min(sayfa.page * sayfa.pageSize, sayfa.total);

  if (yukleniyor) return <Yukleniyor satir={8} />;

  if (veri.length === 0) {
    return <BosDurum baslik={bosBaslik} aciklama={bosAciklama} eylem={bosEylem} />;
  }

  return (
    <div className="kap flex flex-col">
      {/* Tazeleme çizgisi: içerik yerinde kalır, "bir şey oluyor" görünür olur. */}
      <div className="relative h-0.5 overflow-hidden" aria-hidden="true">
        {tazeleniyor && <div className="h-full w-1/3 animate-pulse rounded-full bg-[var(--accent)]" />}
      </div>

      {/* ---- Geniş kap: tablo ---- */}
      <div className="yatay-kaydir hidden @3xl:block">
        <table className="w-full border-collapse text-left">
          <thead>
            {tablo.getHeaderGroups().map((grup) => (
              <tr key={grup.id} className="border-b border-[var(--line)] bg-[var(--surface-sunken)]">
                {grup.headers.map((baslik) => {
                  const siralanabilir = baslik.column.getCanSort();
                  const yon = baslik.column.getIsSorted();
                  const sagaYasli = baslik.column.columnDef.meta?.sagaYasli === true;
                  return (
                    <th key={baslik.id} scope="col" className={cn('px-5 py-3', sagaYasli && 'text-right')}>
                      {baslik.isPlaceholder ? null : siralanabilir ? (
                        <button
                          type="button"
                          onClick={baslik.column.getToggleSortingHandler()}
                          className={cn(
                            'ust-etiket inline-flex items-center gap-1 rounded-[var(--radius-sm)]',
                            'px-1 py-0.5 -mx-1 hover:text-[var(--ink)]',
                            yon && 'text-[var(--accent)]'
                          )}
                          // Ekran okuyucu sıralama yönünü duysun.
                          aria-sort={yon === 'asc' ? 'ascending' : yon === 'desc' ? 'descending' : 'none'}
                        >
                          {flexRender(baslik.column.columnDef.header, baslik.getContext())}
                          <span className="-mt-px shrink-0" aria-hidden="true">
                            {yon === 'asc' ? (
                              <ArrowUp size={12} />
                            ) : yon === 'desc' ? (
                              <ArrowDown size={12} />
                            ) : (
                              <ChevronsUpDown size={12} className="opacity-40" />
                            )}
                          </span>
                        </button>
                      ) : (
                        <span className="ust-etiket">
                          {flexRender(baslik.column.columnDef.header, baslik.getContext())}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {tablo.getRowModel().rows.map((satir: Row<T>) => (
              <tr
                key={satirAnahtari(satir.original)}
                onClick={onSatirTikla ? () => onSatirTikla(satir.original) : undefined}
                className={cn(
                  'border-b border-[var(--line)] last:border-0',
                  onSatirTikla && 'cursor-pointer hover:bg-[var(--surface-hover)]'
                )}
                style={{ height: 'var(--satir-yuksekligi)' }}
              >
                {satir.getVisibleCells().map((hucre) => (
                  <td
                    key={hucre.id}
                    className={cn(
                      // Yatay dolgu 20px: ölçüldü (Vuexy `$table-cell-padding-x: 1.25rem`).
                      // 16px'te sütunlar birbirine yapışıyor, göz ayıramıyor.
                      'px-5 py-3 align-middle',
                      hucre.column.columnDef.meta?.sagaYasli === true && 'text-right'
                    )}
                  >
                    {flexRender(hucre.column.columnDef.cell, hucre.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---- Dar kap: kart listesi ----
           Telefonda yedi sütunlu tabloyu yatay kaydırtmak kullanılabilir değil;
           aynı veri dikey bir kartta okunur. Kolon tanımları aynı kalır. */}
      <ul className="flex flex-col gap-2 p-3 @3xl:hidden">
        {veri.map((satir) => (
          <li key={satirAnahtari(satir)}>
            <button
              type="button"
              onClick={onSatirTikla ? () => onSatirTikla(satir) : undefined}
              disabled={!onSatirTikla}
              className={cn(
                'w-full rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface)] p-3 text-left',
                onSatirTikla && 'hover:border-[var(--line-strong)] hover:bg-[var(--surface-hover)]'
              )}
            >
              {kartGoster ? kartGoster(satir) : null}
            </button>
          </li>
        ))}
      </ul>

      {/* ---- Sayfalama ----
           Telefonda alt alta, geniş ekranda yan yana. Sayfa numarası kutusu
           yerine "önceki/sonraki" + sayaç: dokunma hedefi büyük kalsın. */}
      <div className="flex flex-col gap-3 border-t border-[var(--line)] px-5 py-4 @2xl:flex-row @2xl:items-center @2xl:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-[var(--ink-muted)]">
            <span className="rakam">{ilkKayit}</span>–<span className="rakam">{sonKayit}</span> /{' '}
            <span className="rakam">{sayfa.total}</span> kayıt
          </span>
          <label className="flex items-center gap-2 text-sm text-[var(--ink-muted)]">
            <span className="sr-only @sm:not-sr-only">Sayfa başına</span>
            <select
              value={sayfa.pageSize}
              onChange={(olay) => onSayfaBoyu(Number(olay.target.value))}
              className="rounded-[var(--radius-sm)] border border-[var(--line-strong)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[var(--ink)]"
            >
              {SAYFA_BOYLARI.map((boy) => (
                <option key={boy} value={boy}>
                  {boy} satır
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center justify-between gap-2 @2xl:justify-end">
          <Dugme boy="sm" disabled={sayfa.page <= 1} onClick={() => onSayfa(sayfa.page - 1)}>
            Önceki
          </Dugme>
          <span className="rakam text-sm text-[var(--ink-muted)]">
            {sayfa.page} / {sayfa.totalPages}
          </span>
          <Dugme boy="sm" disabled={sayfa.page >= sayfa.totalPages} onClick={() => onSayfa(sayfa.page + 1)}>
            Sonraki
          </Dugme>
        </div>
      </div>
    </div>
  );
}

/** Kart görünümünde bir alan/değer satırı. */
export function KartAlani({ etiket, children }: { etiket: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="shrink-0 text-xs text-[var(--ink-muted)]">{etiket}</span>
      <span className="min-w-0 truncate text-sm text-[var(--ink)]">{children}</span>
    </div>
  );
}

/**
 * Kolon tanımına kendi alanlarımızı ekler.
 * TanStack `meta` alanını bu modül genişletmesiyle tipler; böylece
 * `meta: { sagaYasli: true }` yazarken tip denetimi çalışır.
 */
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Sayı sütunu — sağa yaslanır (docs/13 bölüm 5, tabular-nums ile birlikte). */
    sagaYasli?: boolean;
    /** Bu tip parametreleri kütüphane imzasından gelir; kullanılmaları gerekmez. */
    _tData?: TData;
    _tValue?: TValue;
  }
}
